/* eslint-disable react-hooks/exhaustive-deps */
import React, { ChangeEvent, memo, useCallback, useEffect, useState } from 'react'
import {
  StandardTextFieldProps,
  FilledTextFieldProps,
  OutlinedTextFieldProps,
} from '@maas-components/core'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'
import { useDebouncedCallback } from 'use-debounce'
import Typography from 'core/components/Typography/index'
import { HelperTextWrapper, StyledTextField as MaasTextField } from './style'

type TStyledHelperTextProps = {
  children: React.ReactNode | string
}

interface BaseStyledTextFieldProps {
  maskFunction?: (value: string | number) => string | undefined
  helperText?: string
  type?: 'text' | 'number'
  min?: number
  error?: boolean
  delay?: number
  value?: string
  sanitize?: boolean
  onChange: (event: ChangeEvent<HTMLInputElement>) => void
}

type StandardStyledTextFieldProps = Omit<StandardTextFieldProps, 'onChange'> &
  BaseStyledTextFieldProps

type FilledStyledTextFieldProps = Omit<FilledTextFieldProps, 'onChange'> & BaseStyledTextFieldProps

type OutlinedStyledTextFieldProps = Omit<OutlinedTextFieldProps, 'onChange'> &
  BaseStyledTextFieldProps

type IStyledTextFieldProps =
  | StandardStyledTextFieldProps
  | FilledStyledTextFieldProps
  | OutlinedStyledTextFieldProps

const StyledHelperText = ({ children }: TStyledHelperTextProps) => (
  <HelperTextWrapper>
    <Typography variant="caption" paragraph={false}>
      <InfoOutlinedIcon fontSize="small" />
      {children}
    </Typography>
  </HelperTextWrapper>
)

const StyledTextField = ({
  maskFunction,
  helperText = '',
  type = 'text',
  min = 0,
  error = false,
  delay = 600,
  value = '',
  sanitize = false,
  ...props
}: IStyledTextFieldProps) => {
  const [innerValue, setInnerValue] = useState('')

  const helperTextEnabled = helperText && !error
  const styledHelperText = helperTextEnabled ? (
    <StyledHelperText>{helperText}</StyledHelperText>
  ) : (
    helperText
  )

  useEffect(() => {
    // 0 é um valor válido, por isso não devemos usar if(value) apenas
    if (value !== null && value !== undefined) {
      setInnerValue(value)
    } else {
      setInnerValue('')
    }
  }, [value])

  const debouncedHandleOnChange = useDebouncedCallback(event => {
    props.onChange(event)
  }, delay)

  const persistHandleOnChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      event.persist()

      let sanitizedValue = event.currentTarget.value

      if (sanitize) {
        // eslint-disable-next-line no-useless-escape
        sanitizedValue = sanitizedValue.replace(/[@#%$&!*()[\]+\-{}=:¨"'¹²³£¢¬]/g, '')
      }

      const newValue = maskFunction ? maskFunction(sanitizedValue) : sanitizedValue

      setInnerValue(newValue || '')

      const sanitizedEvent = {
        ...event,
        currentTarget: {
          ...event.currentTarget,
          value: sanitizedValue,
        },
      }

      debouncedHandleOnChange.callback(sanitizedEvent)
    },
    [debouncedHandleOnChange, maskFunction]
  )

  const handleOnChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { max } = props?.InputProps?.inputProps ?? {}

    if (type === 'number') {
      const currentValue = parseFloat(event.currentTarget.value)

      if (max && currentValue >= parseFloat(max)) {
        return
      }

      if (currentValue >= min || Number.isNaN(currentValue)) {
        persistHandleOnChange(event)
      }
    } else {
      persistHandleOnChange(event)
    }
  }

  return (
    <MaasTextField
      {...props}
      type={type}
      onChange={handleOnChange}
      value={innerValue}
      error={error}
      helperText={styledHelperText}
    />
  )
}

const withMask = (
  maskFunction: (value: string | number) => string | undefined,
  defaultValue = '0,00',
  delay = 600
) => {
  const WrappedTextField = ({ onChange, value, ...props }: IStyledTextFieldProps) => {
    const [maskedValue, setMaskedValue] = useState(value ? maskFunction(value) : '')

    useEffect(() => {
      setMaskedValue(maskFunction(value || defaultValue))
    }, [value])

    const fieldValue = value ? maskFunction(value) : defaultValue

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
      const tmpValue = maskFunction(event.target.value)
      setMaskedValue(tmpValue)
      onChange(event)
    }
    return (
      <StyledTextField
        delay={delay}
        maskFunction={maskFunction}
        value={maskedValue || fieldValue}
        onChange={handleChange}
        {...props}
      />
    )
  }

  return WrappedTextField
}

export { withMask, StyledHelperText }

export default memo(StyledTextField)
